import { jwtDecode } from 'jwt-decode';

// The AuthError leaf, never the `api` barrel — importing the barrel from here
// re-enters core's circular import chain.
import { AuthError } from '../../api/errors';
import { AsyncStorage } from '../AsyncStorage';
import { Sentry } from '../sentry';

/**
 * The seam between core's HTTP layer and a platform DPoP signer (RFC 9449).
 *
 * Core never signs anything itself: a platform (today only web, see
 * packages/web/src/services/dpop) hands in a signer through `setDpopSigner`,
 * and everything here degrades to plain bearer tokens when there is none.
 * Mobile and the legacy /auth path never set one, so their requests are
 * byte-identical to what they were before DPoP existed.
 *
 * The scheme follows the *token*, not the feature flag: an access token that
 * carries `cnf.jkt` is key-bound and must be sent as `DPoP <token>` with a
 * proof; an unbound token is sent as `Bearer <token>` and the signer is never
 * consulted (so a legacy, mobile or flag-off user never opens IndexedDB).
 *
 * A signer is `{ getThumbprint(): Promise<string | null>,
 * createProof({ htm, htu, accessToken?, nonce? }): Promise<string> }`.
 */

const DPOP_PROOF_REJECTED_ERROR = 'invalid_dpop_proof';
const DPOP_NONCE_REQUIRED_ERROR = 'use_dpop_nonce';

// The `error="…"` of an API 401's `WWW-Authenticate: DPoP …` challenge, e.g.
// `DPoP error="invalid_dpop_proof", error_description="…"`.
const DPOP_CHALLENGE_ERROR = /\berror="?([a-z_]+)"?/i;

// RFC 9449 §4.2: `htu` is the request URI without query and fragment. Callers
// hand in the URL they are about to request, so strip it here rather than at
// every call site.
const toHtu = (url) => String(url).split(/[?#]/)[0];

let signer = null;

/**
 * Nonces (RFC 9449 §8 and §9). A server that wants one rejects a proof
 * without it as `use_dpop_nonce`, names the nonce it wants in a `DPoP-Nonce`
 * response header, and may hand out a fresh one on any later response; the
 * client puts the latest one in every proof it signs for that server. The
 * `/oidc` token endpoint has required one since 2026-09-11 (dev), with a
 * lifetime of 3–4 minutes. Nonces are opaque and server-scoped — the provider
 * (token and revocation endpoints) and the API each have their own slot.
 *
 * They are kept in the same device store as the tokens (localStorage on web),
 * with an in-memory copy as the fallback: a reload or a second tab then starts
 * with the nonce the last response handed out instead of paying a challenge on
 * every request of its first burst, and because the store is read for every
 * proof, a nonce another tab was just handed is used here too. A nonce is not
 * a secret and not single-use, which is what makes sharing it safe; it is not
 * part of the session either, so logout leaves it alone. A missing or stale
 * nonce costs one challenge round trip — the protocol's own bootstrap.
 */
export const DPOP_NONCE_SCOPE = { PROVIDER: 'provider', API: 'api' };

const nonceStorageKey = (scope) => `dpopNonce:${scope}`;

const nonces = new Map();

const readStoredNonce = async (scope) => {
  try {
    return (await AsyncStorage.getItem(nonceStorageKey(scope))) || null;
  } catch (_) {
    return null;
  }
};

// Fire and forget: the in-memory copy already serves this tab, and a write
// that fails only costs a challenge after the next reload.
const writeStoredNonce = (scope, nonce) => {
  try {
    AsyncStorage.setItem(nonceStorageKey(scope), nonce).catch(() => {});
  } catch (_) {
    // Storage unavailable — see above.
  }
};

const forgetStoredNonce = (scope) => {
  try {
    AsyncStorage.removeItem(nonceStorageKey(scope)).catch(() => {});
  } catch (_) {
    // Storage unavailable — nothing to forget there.
  }
};

export const setDpopSigner = (nextSigner) => {
  signer = nextSigner || null;
};

export const getDpopSigner = () => signer;

// Test hook: forget the signer and every remembered nonce, stored or not.
export const resetDpopSigner = () => {
  signer = null;
  nonces.clear();
  Object.values(DPOP_NONCE_SCOPE).forEach(forgetStoredNonce);
};

/**
 * The nonce to sign into the next proof for `scope`: the stored one first
 * (shared with the other tabs, so the freshest), else this tab's memory, else
 * `null`.
 */
export const getDpopNonce = async (scope) =>
  (await readStoredNonce(scope)) ?? nonces.get(scope) ?? null;

/**
 * Keep the `DPoP-Nonce` a response carried, if any, as the nonce for the next
 * proof signed for `scope`. Returns that nonce, or `null` when the response
 * had none — the previously remembered one is then left in place.
 */
export const rememberDpopNonce = (scope, headers) => {
  const nonce =
    typeof headers?.get === 'function' ? headers.get('DPoP-Nonce') : null;
  if (!nonce) return null;

  if (nonces.get(scope) !== nonce) {
    nonces.set(scope, nonce);
    writeStoredNonce(scope, nonce);
  }
  return nonce;
};

/**
 * Sign one proof for one request, carrying the nonce remembered for the
 * server it goes to (when there is one) and `ath` when a token is presented.
 */
const signProof = async ({ method, url, accessToken, nonceScope }) => {
  const request = { htm: method.toUpperCase(), htu: toHtu(url) };
  if (accessToken) {
    request.accessToken = accessToken;
  }
  const nonce = await getDpopNonce(nonceScope);
  if (nonce) {
    request.nonce = nonce;
  }
  return signer.createProof(request);
};

/**
 * The configured signer's key thumbprint (RFC 7638), or `null` when there is
 * no signer or it has no usable key.
 */
export const getDpopThumbprint = async () =>
  signer ? signer.getThumbprint() : null;

/**
 * The `cnf.jkt` confirmation claim of an access token — the thumbprint of the
 * key it is bound to — or `null` when the token is unbound or undecodable.
 */
export const getAccessTokenKeyBinding = (accessToken) => {
  if (!accessToken) return null;

  try {
    return jwtDecode(accessToken)?.cnf?.jkt ?? null;
  } catch (_) {
    return null;
  }
};

/**
 * Can this access token still be used from this client? An unbound token
 * always can, without touching the signer; a bound one only while the
 * configured key is the one it was bound to. Callers treat `false` the same
 * way they treat an expiring token: refresh instead of sending it.
 */
export const isAccessTokenUsableWithDpopKey = async (accessToken) => {
  const boundTo = getAccessTokenKeyBinding(accessToken);
  if (!boundTo) return true;

  return (await getDpopThumbprint()) === boundTo;
};

/**
 * The authorization headers for one API request: `Authorization: Bearer …`
 * for an unbound token (unchanged from before DPoP, including the literal
 * `Bearer undefined` of a request without a token), `Authorization: DPoP …`
 * plus a freshly signed `DPoP` proof for a bound one.
 *
 * A bound token without a matching key throws an AuthError with the code
 * `dpop_key_unavailable`. Guard-rail: this must never be thrown from inside a
 * token exchange — `isOidcGrantRejected` would read a 4xx-shaped AuthError as
 * a rejected grant and clear the session. It is only ever raised while
 * attaching a token to a resource request.
 */
export const buildAuthorizationHeaders = async ({
  accessToken,
  method,
  url,
}) => {
  const boundTo = getAccessTokenKeyBinding(accessToken);
  if (!boundTo) {
    return { Authorization: `Bearer ${accessToken}` };
  }

  if ((await getDpopThumbprint()) !== boundTo) {
    const error = new AuthError('DPoP key for the access token is unavailable');
    error.code = 'dpop_key_unavailable';
    throw error;
  }

  return {
    Authorization: `DPoP ${accessToken}`,
    DPoP: await signProof({
      method,
      url,
      accessToken,
      nonceScope: DPOP_NONCE_SCOPE.API,
    }),
  };
};

/**
 * The `DPoP` header for a request that carries no access token — the /oidc
 * token and revocation POSTs — or `{}` when there is no key, which the
 * provider accepts (DPoP is per request).
 */
export const buildDpopProofHeader = async ({ method, url }) => {
  const thumbprint = await getDpopThumbprint();
  if (!thumbprint) return {};

  return {
    DPoP: await signProof({
      method,
      url,
      nonceScope: DPOP_NONCE_SCOPE.PROVIDER,
    }),
  };
};

/**
 * The OAuth error a server answered with, read from any of the three shapes
 * the call sites hold: an AuthError mapped from an OAuth error body, the raw
 * TokenRequestError carrying that body, or an API 401 whose `WWW-Authenticate`
 * challenge names the error. `null` for anything else — never throws.
 */
const getDpopErrorCode = (errorOrResponse) => {
  if (!errorOrResponse) return null;

  if (errorOrResponse.code) return errorOrResponse.code;
  if (errorOrResponse.response?.error) return errorOrResponse.response.error;

  const { status, headers } = errorOrResponse;
  if (status !== 401 || typeof headers?.get !== 'function') return null;

  const match = DPOP_CHALLENGE_ERROR.exec(
    headers.get('WWW-Authenticate') || ''
  );
  return match ? match[1] : null;
};

/** Did the server reject our proof? */
export const isDpopProofRejected = (errorOrResponse) =>
  getDpopErrorCode(errorOrResponse) === DPOP_PROOF_REJECTED_ERROR;

/**
 * Does the server want a nonce in the proof (RFC 9449 §8 / §9)? The nonce it
 * wants is in the `DPoP-Nonce` header of the same response — see
 * `rememberDpopNonce`. A challenge is answered by signing a new proof and
 * sending the same request once more; it says nothing about the token or the
 * grant, which the server has not looked at yet.
 */
export const isDpopNonceRequired = (errorOrResponse) =>
  getDpopErrorCode(errorOrResponse) === DPOP_NONCE_REQUIRED_ERROR;

export const getDpopProofIssuedAt = (proof) => {
  if (!proof) return undefined;

  try {
    return jwtDecode(proof).iat;
  } catch (_) {
    return undefined;
  }
};

/**
 * Clock-skew telemetry (see the DPoP task's decision 8): a browser clock
 * outside the provider's `iat` tolerance makes every proof invalid, which
 * reads as "session gone" and locks the user out. Recording the client's
 * `iat` next to the client's current time on every rejection makes that
 * visible before anyone reports it.
 */
export const reportDpopProofRejected = ({ endpointKind, htu, proof }) => {
  Sentry.captureMessage('DPoP proof rejected', {
    level: 'warning',
    tags: {
      internal_system: 'authentication',
      token_provider: 'oidc',
      dpop_endpoint: endpointKind,
    },
    extra: {
      endpoint_kind: endpointKind,
      htu,
      client_iat: getDpopProofIssuedAt(proof),
      client_now: Math.floor(Date.now() / 1000),
    },
  });
};

/**
 * One breadcrumb per answered nonce challenge, so a login or request that
 * took two round trips is explainable from Sentry without being an error.
 */
export const reportDpopNonceChallenge = ({ endpointKind, htu }) => {
  Sentry.addBreadcrumb({
    category: 'auth',
    level: 'info',
    message: 'DPoP nonce challenge answered',
    data: { endpoint_kind: endpointKind, htu },
  });
};
